import { APPSTATE, ROOMSSTATE, ROUTESTATE } from '../..'
import { RoomStateItem } from '../../../../types/state'
import { setColorScheme } from '../../../actions/app'
import { setColorSchemeLocked } from '../../../actions/layout'
import { saveSettings } from '../../../actions/settings'
import { appAction } from '../../../actions/workerActions'
import {
    AppAction,
    ColorScheme,
    RoomEvent,
    SettingsType,
    ViewMode,
} from '../../../constants/enums'
import { FULL_WINDOW_CLASS, THEATER_MODE_CLASS } from '../../../constants/video'
import { trackAnalyticsEvent } from '../../../utilities/analytics'
import { customStateWrapper } from '../../customStateWrapper'
import { getModalState, getState } from '../../getState'
import { updateFloatingRoom } from '../../modals/state.floater'
import { pubSubRooms } from '../../pubSub'
import { clearRoomPendingReply } from '../../state.rooms'
import { setScreenOrientation } from '../../state.screen'
import { closeWhiteboard } from '../../state.whiteboard'
import './events'

type RoomStateWrapperAction = {
    id: number
    room: Partial<RoomStateItem>
}
type SetRoomStateOptions = {
    deleteRoom?: boolean
    immediate?: boolean
    skipEmit?: boolean
}

export const setRoomState = customStateWrapper<
    RoomStateWrapperAction,
    SetRoomStateOptions
>(({ room }, { immediate = false, skipEmit = false } = {}) => {
    if (!skipEmit) {
        pubSubRooms.emit(RoomEvent.STATE, ROOMSSTATE.rooms, immediate)
        if (room && room.loungePreviews) {
            pubSubRooms.emit(RoomEvent.PREVIEWS, ROOMSSTATE.rooms, false)
        }
    }
})(({ id, room }, { deleteRoom = false } = {}) => {
    if (deleteRoom) {
        const { [id]: _deletedRoom, ...remainingRooms } = ROOMSSTATE.rooms
        ROOMSSTATE.rooms = remainingRooms
    } else {
        ROOMSSTATE.rooms = {
            ...ROOMSSTATE.rooms,
            [id]: {
                ...ROOMSSTATE.rooms[id],
                ...room,
            },
        }
    }
})

export const setPinnedRooms = customStateWrapper<Array<number>>(() => {
    pubSubRooms.emit(RoomEvent.PIN_ROOM, ROOMSSTATE.pinnedRooms)
    setScreenOrientation({ overrideSkip: true })
})((rooms) => {
    ROOMSSTATE.pinnedRooms = rooms
})

export const setPinHelperID = customStateWrapper<number>((id) => {
    pubSubRooms.emit(RoomEvent.PIN_HELPER_ID, id, !!id)
})((id) => {
    ROOMSSTATE.pinHelperID = id
})

export const setRoomsRestored = customStateWrapper<number>((restored) => {
    pubSubRooms.emit(RoomEvent.RESTORED, restored)
})((restored) => {
    ROOMSSTATE.restored = restored
})

type SetRoomViewModeOptions = {
    /** The mode an orientation change displaced, restored when the
     *  orientation comes back. Explicit changes omit it, which clears it. */
    restoreViewMode?: ViewMode | null
}

export const setRoomViewMode = (
    viewMode: ViewMode,
    saveSetting?: boolean,
    { restoreViewMode = null }: SetRoomViewModeOptions = {},
) => {
    const floatingRoom = getModalState().floating_room
    const isMobile = APPSTATE.app.isMobile
    // on mobile, Theater is the portrait mobile theater layout: dark, locked.
    // keyed on isMobile alone (not orientation): handleViewportChange runs
    // resolveFullWindow before setScreenOrientation, so the stored `portrait`
    // is still the old one by the time the rotate-out unlock runs here
    const wasMobileTheater =
        floatingRoom.viewMode === ViewMode.Theater && isMobile
    const isMobileTheater = viewMode === ViewMode.Theater && isMobile
    const isOverlay =
        viewMode === ViewMode.FullWindow || viewMode === ViewMode.Theater

    if (isMobileTheater && !wasMobileTheater) {
        setColorScheme(ColorScheme.dark)
        setColorSchemeLocked(true)
    }
    setFullWindow(viewMode === ViewMode.FullWindow, saveSetting)
    setTheater(viewMode === ViewMode.Theater)
    if (wasMobileTheater && !isMobileTheater) {
        setColorSchemeLocked(false)
        // full window sets its own dark scheme in setFullWindow
        if (viewMode !== ViewMode.FullWindow) {
            setColorScheme(
                getState().SETTINGSSTATE[SettingsType.DEFAULT].colorScheme,
            )
        }
    }

    updateFloatingRoom({
        viewMode,
        restoreViewMode,
        expanded: isOverlay ? true : floatingRoom.expanded,
        // leaving mobile theater by choice lands on the unzoomed portrait
        // room, as the zoom exit always has; an orientation displacement
        // (restoreViewMode set) keeps the zoom for the way back
        ...(wasMobileTheater &&
            !isMobileTheater &&
            restoreViewMode === null && { showZoomVideo: false }),
    })
    if (isOverlay) {
        // Replying isn't allowed in overlay views, so drop any pending reply
        // for the active room as we enter.
        if (floatingRoom.id) {
            clearRoomPendingReply(floatingRoom.id)
        }
    }
    setScreenOrientation({ overrideSkip: true })
}

const setFullWindow = (fullWindow: boolean, saveSetting?: boolean) => {
    appAction(AppAction.FULL_WINDOW, fullWindow)
    const floatingRoom = getModalState().floating_room
    const fullWindowState = floatingRoom.viewMode === ViewMode.FullWindow
    const { fullWindow: fullWindowSetting } =
        getState().SETTINGSSTATE[SettingsType.DEFAULT]
    if (fullWindow !== fullWindowState) {
        if (!fullWindow) closeWhiteboard()
        // setModal(ModalKind.FloatingRoom, {
        //     ...floatingRoom,
        //     viewMode: fullWindow ? ViewMode.FullWindow : ViewMode.Normal,
        // })
        if (fullWindow) {
            document.body.classList.remove(FULL_WINDOW_CLASS)
            document.body.classList.add(FULL_WINDOW_CLASS)
            setColorScheme(ColorScheme.dark)
        } else {
            document.body.classList.remove(FULL_WINDOW_CLASS)
            const colorSchemeSetting =
                getState().SETTINGSSTATE[SettingsType.DEFAULT].colorScheme
            setColorScheme(colorSchemeSetting)
        }
    }
    if (saveSetting && fullWindow !== fullWindowSetting) {
        const resolvedPath = ROUTESTATE.visiblePage.replace('message', 'pm')
        trackAnalyticsEvent({
            action: `FullWindowMode__Enabled`,
            data: { fullWindow, resolvedPath },
        })
        saveSettings(SettingsType.DEFAULT, 'fullWindow', fullWindow)
    }
}

const setTheater = (theater: boolean) => {
    const floatingRoom = getModalState().floating_room
    const theaterModeState = floatingRoom.viewMode === ViewMode.Theater
    if (theater !== theaterModeState) {
        if (theater) {
            document.body.classList.remove(THEATER_MODE_CLASS)
            document.body.classList.add(THEATER_MODE_CLASS)
        } else {
            document.body.classList.remove(THEATER_MODE_CLASS)
        }
    }
}

export const onRouteChangeFullWindow = (url: string) => {
    const isRoomPage = url.startsWith('/room/')
    const isMessagePage = url.startsWith('/message/')
    if (!isRoomPage && !isMessagePage) {
        setFullWindow(false)
    }
}
